import { useApplications } from '@/app/hooks/data-hooks/useApplications'
import { useOrders } from '@/app/hooks/data-hooks/useOrders'
import { useAuth } from '@/app/providers/Auth/Auth'
import { ApplicationStatus } from '@/types/application'
import { ICourse } from '@/types/course'
import { OrderPaymentTerm, OrderStatus } from '@/types/order'

type UseCourseApplicationStatus = {
  courseInfo: Pick<ICourse, '_id'>
}

export function useCourseApplicationStatus({
  courseInfo,
}: UseCourseApplicationStatus) {
  const { userId, isLoading: isAuthLoading } = useAuth()

  const {
    refetch: refetchOrders,
    data: { data: orders = [] } = {},
    isLoading: isLoadingOrders,
  } = useOrders(
    {
      itemId: courseInfo?._id,
      userId: userId!,
      status: [OrderStatus.Completed, OrderStatus.Pending],
    },
    undefined,
    {
      enabled: !!userId,
    }
  )

  const {
    refetch: refetchApplications,
    data: { data: applications = [] } = {},
    isLoading: isLoadingApplications,
  } = useApplications(
    {
      courseId: courseInfo?._id,
      userId: userId!,
      isRejected: false,
    },
    undefined,
    {
      enabled: !!userId,
    }
  )

  const [application] = applications
  const [order] = orders
  const applicationLastStatus = application?.statuses?.at(-1)

  const userAlreadyApplied =
    (!!application &&
      applicationLastStatus &&
      applicationLastStatus !== ApplicationStatus.Cancelled) ||
    (!!order &&
      order.status !== OrderStatus.Cancelled &&
      (order.paymentTerm === OrderPaymentTerm.FixPay ||
        order.paymentTerm === OrderPaymentTerm.Isa ||
        (order.paymentTerm === OrderPaymentTerm.Free &&
          order.status === OrderStatus.Completed) ||
        (order.paymentTerm === OrderPaymentTerm.Upfront &&
          order.status !== OrderStatus.Completed)))

  const refetch = () => Promise.all([refetchApplications(), refetchOrders()])

  return {
    userAlreadyApplied,
    orders,
    applications,
    refetch,
    isLoading: isAuthLoading || isLoadingApplications || isLoadingOrders,
  }
}
